// src/components/map/hooks/adapters/useRigFracIconSize.ts
//
// Adapter hook: rig/frac layer icon size.
//
// The single runtime owner of the Rigs / Frac Crews `icon-size`, and of the
// `icon-halo-width` outline that scales with it. It composes
// two inputs into one expression per layer: the user-configured size multiplier
// (per layer, from the map store display settings) and the currently hovered
// feature id (shared store state written by the hover-tooltip adapter). Because
// both flow through this one hook, a hover never resets a user's custom size and
// a size change never cancels the hover enlargement. A multiplier of 1 with
// nothing hovered leaves the default appearance unchanged. Re-applies after a
// style reload by keying on the MapContext isStyleLoaded flag (the same
// lifecycle that re-adds the layers), guarding every map call with
// map.getLayer.
import { useEffect } from "react";

import { FRAC_LAYER, RIG_LAYER } from "constants/mapLayers.constants";
import { selectDisplaySettings, selectHoveredRigFracId, useMapStore } from "stores/map";

import {
  FRAC_OUTLINE_PX_PER_ICON_SIZE,
  RIG_OUTLINE_PX_PER_ICON_SIZE,
  buildRigFracHaloWidthExpression,
  buildRigFracIconSizeExpression
} from "../../utils/rigFracIconSizeExpressions";
import { useMapContext } from "../useMapContext";

export interface UseRigFracIconSizeParams {
  // Gated by the "Rig And Frac Layers" beta flag at the mount site.
  enabled?: boolean;
}

export function useRigFracIconSize(params: UseRigFracIconSizeParams = {}): void {
  const { enabled = true } = params;

  const { mapbox, isMapInitialized, isStyleLoaded } = useMapContext();
  const { rigIconSize, fracIconSize } = useMapStore(selectDisplaySettings);
  const hoveredId = useMapStore(selectHoveredRigFracId);

  useEffect(() => {
    if (!enabled || !mapbox || !isMapInitialized || !isStyleLoaded) {
      return;
    }

    const sizeByLayer: Record<string, number> = {
      [RIG_LAYER]: rigIconSize,
      [FRAC_LAYER]: fracIconSize
    };
    const outlineByLayer: Record<string, number> = {
      [RIG_LAYER]: RIG_OUTLINE_PX_PER_ICON_SIZE,
      [FRAC_LAYER]: FRAC_OUTLINE_PX_PER_ICON_SIZE
    };

    for (const mapLayerId of [RIG_LAYER, FRAC_LAYER]) {
      if (!mapbox.getLayer(mapLayerId)) {
        continue;
      }

      mapbox.setLayoutProperty(
        mapLayerId,
        "icon-size",
        buildRigFracIconSizeExpression({
          sizeMultiplier: sizeByLayer[mapLayerId],
          hoveredId
        })
      );
      mapbox.setPaintProperty(
        mapLayerId,
        "icon-halo-width",
        buildRigFracHaloWidthExpression(outlineByLayer[mapLayerId], {
          sizeMultiplier: sizeByLayer[mapLayerId],
          hoveredId
        })
      );
    }
  }, [
    enabled,
    mapbox,
    isMapInitialized,
    isStyleLoaded,
    rigIconSize,
    fracIconSize,
    hoveredId
  ]);
}
